import { supabase } from '../lib/supabase';

export const adminService = {
  // Check if current user is admin
  async isAdmin() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return false;

      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('is_admin')
        ?.eq('id', user?.id)
        ?.single();

      if (error) throw error;
      return data?.is_admin || false;
    } catch (error) {
      console.error('Admin check error:', error);
      return false;
    }
  },

  // Get webhook events with filtering
  async getWebhookEvents({ limit = 50, eventType = null, status = null, startDate = null, endDate = null } = {}) {
    try {
      let query = supabase
        ?.from('webhook_events')
        ?.select('*')
        ?.order('created_at', { ascending: false })
        ?.limit(limit);

      if (eventType) {
        query = query?.eq('event_type', eventType);
      }

      if (status) {
        query = query?.eq('processing_status', status);
      }

      if (startDate) {
        query = query?.gte('created_at', startDate);
      }

      if (endDate) {
        query = query?.lte('created_at', endDate);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Convert to camelCase
      return data?.map(event => ({
        id: event?.id,
        eventType: event?.event_type,
        eventId: event?.event_id,
        payload: event?.payload,
        processingStatus: event?.processing_status,
        errorMessage: event?.error_message,
        createdAt: event?.created_at,
        processedAt: event?.processed_at
      }));
    } catch (error) {
      console.error('Get webhook events error:', error);
      throw error;
    }
  },

  // Get edge function logs with filtering
  async getEdgeFunctionLogs({ limit = 50, functionName = null, logLevel = null, startDate = null, endDate = null } = {}) {
    try {
      let query = supabase
        ?.from('edge_function_logs')
        ?.select('*')
        ?.order('created_at', { ascending: false })
        ?.limit(limit);

      if (functionName) {
        query = query?.eq('function_name', functionName);
      }

      if (logLevel) {
        query = query?.eq('log_level', logLevel);
      }

      if (startDate) {
        query = query?.gte('created_at', startDate);
      }

      if (endDate) {
        query = query?.lte('created_at', endDate);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Convert to camelCase
      return data?.map(log => ({
        id: log?.id,
        functionName: log?.function_name,
        logLevel: log?.log_level,
        message: log?.message,
        metadata: log?.metadata,
        userId: log?.user_id,
        createdAt: log?.created_at
      }));
    } catch (error) {
      console.error('Get edge function logs error:', error);
      throw error;
    }
  },

  // Get JWT validation failures with filtering
  async getJwtValidationFailures({ limit = 50, errorType = null, startDate = null, endDate = null } = {}) {
    try {
      let query = supabase
        ?.from('jwt_validation_failures')
        ?.select('*')
        ?.order('created_at', { ascending: false })
        ?.limit(limit);

      if (errorType) {
        query = query?.eq('error_type', errorType);
      }

      if (startDate) {
        query = query?.gte('created_at', startDate);
      }

      if (endDate) {
        query = query?.lte('created_at', endDate);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Convert to camelCase
      return data?.map(failure => ({
        id: failure?.id,
        userId: failure?.user_id,
        errorType: failure?.error_type,
        errorMessage: failure?.error_message,
        tokenPreview: failure?.token_preview,
        requestPath: failure?.request_path,
        ipAddress: failure?.ip_address,
        userAgent: failure?.user_agent,
        metadata: failure?.metadata,
        createdAt: failure?.created_at
      }));
    } catch (error) {
      console.error('Get JWT validation failures error:', error);
      throw error;
    }
  },

  // Get monitoring statistics
  async getMonitoringStats() {
    try {
      const [webhookStats, logStats, jwtStats] = await Promise.all([
        // Webhook stats
        supabase?.from('webhook_events')?.select('processing_status', { count: 'exact', head: true }),
        // Log stats
        supabase?.from('edge_function_logs')?.select('log_level', { count: 'exact', head: true }),
        // JWT failure stats
        supabase?.from('jwt_validation_failures')?.select('error_type', { count: 'exact', head: true })
      ]);

      return {
        totalWebhookEvents: webhookStats?.count || 0,
        totalLogs: logStats?.count || 0,
        totalJwtFailures: jwtStats?.count || 0
      };
    } catch (error) {
      console.error('Get monitoring stats error:', error);
      return {
        totalWebhookEvents: 0,
        totalLogs: 0,
        totalJwtFailures: 0
      };
    }
  },

  // Format date for display
  formatDate(dateString) {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date?.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  },

  // Format relative time
  formatRelativeTime(dateString) {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  }
};